import React, { useEffect, useRef, useState, MouseEvent, JSX } from 'react';
import { Button, Input } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import {
  ApiAirportType,
  ApiFlightSearchGeoUnitsRequestFromJSON,
  ApiSearchGeoUnitsRequestFromJSON,
  ApiGeoUnit,
  ApiItemType
} from '@ibe/api';
import { LoggerFactory } from '@ibe/services';
import fallback from '../../../../_Translations/generated/geo-unit-checkbox-async.en.t.json';
import Keys from '../../../../_Translations/generated/geo-unit-checkbox-async.en.t.json.keys';
import useTranslation from '../../../Hooks/useTranslation';
import Spinner from '../../UI/LoadingOverlay/Spinner';
import useApi from '../../../Hooks/useApi';

const logger = LoggerFactory.get('GeoUnitCheckboxAsync.tsx');
import { PSEUDO_CODE, TypeCodeKey, usePseudoGeoOption } from './externals';
import { useClickAway } from 'react-use';
import CustomInput from '../../CustomInput/CustomInput';
export interface Props {
  itemType: ApiItemType;
  airportType?: ApiAirportType;
  value: ApiGeoUnit[] | undefined;
  placeholder?: string;
  onChange: (value: ApiGeoUnit[]) => void;
  name?: string;
  externalGeoUnits?: ApiGeoUnit[];
  changeOnBlur?: boolean;
  withSelectAll?: boolean;
  showPseudo?: boolean;
  withRemoveAll?: boolean;
  staticLabel?: string;
  possibleGeoUnitCodes?: string[];
  disabled?: boolean;
  valueToShow?: string;
}

const GeoUnitCheckboxAsync = (props: Props): JSX.Element => {
  const {
    airportType,
    itemType,
    value = [],
    placeholder,
    name,
    onChange,
    externalGeoUnits,
    changeOnBlur,
    withSelectAll,
    showPseudo,
    withRemoveAll,
    staticLabel,
    possibleGeoUnitCodes,
    disabled,
    valueToShow
  } = props;
  const { t } = useTranslation('geo-unit-checkbox-async', fallback, Keys);
  const api = useApi();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [options, setOptions] = useState<ApiGeoUnit[]>(externalGeoUnits || []);
  const [selectedOptions, setSelectedOptions] = useState<ApiGeoUnit[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const outerRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSelectedOptions(value ? [...value] : []);
  }, [value]);

  const loadOptions = async (): Promise<void> => {
    setIsLoading(true);
    const request = airportType
      ? ApiFlightSearchGeoUnitsRequestFromJSON({
          type: 'FlightSearchGeoUnitsRequest',
          search: '',
          offset: 0,
          limit: 200,
          itemType,
          airportType
        })
      : ApiSearchGeoUnitsRequestFromJSON({
          type: 'SearchGeoUnitsRequest',
          search: '',
          offset: 0,
          limit: 200,
          itemType
        });
    try {
      const result = await api.searchGeoUnits(request);
      setOptions(
        !!possibleGeoUnitCodes && possibleGeoUnitCodes.length > 0
          ? result.geoUnits.reduce((total: ApiGeoUnit[], current: ApiGeoUnit) => {
              let currentTotal = [...total];
              if (possibleGeoUnitCodes.includes(current.code)) {
                currentTotal = [...total, current];
              }
              return [
                ...currentTotal,
                ...Object.entries(current.children)
                  .filter(([, unit]) => possibleGeoUnitCodes.includes(unit.code))
                  .map(([, unit]) => unit)
              ];
            }, [])
          : result.geoUnits
      );
    } catch (err) {
      logger.log(err);
    }
    setIsLoading(false);
  };

  const handleToggle = async (): Promise<void> => {
    if (options.length === 0) {
      await loadOptions();
    }
    if (isOpen) {
      setSelectedOptions([...value]);
    }
    setIsOpen(!isOpen);
  };

  useClickAway(popupRef, (): void => {
    apply();
  });

  const toggleOpen = async (): Promise<void> => {
    if (changeOnBlur && isOpen) {
      onChange([...selectedOptions]);
    }
    await handleToggle();
  };

  const apply = (newOptions?: ApiGeoUnit[]): void => {
    onChange(newOptions ? [...newOptions] : [...selectedOptions]);
    setIsOpen(false);
  };

  const onOptionClick = (option: ApiGeoUnit): void => {
    const foundOption = selectedOptions.find(selectedOption => selectedOption?.id === option.id);
    let newSelectedOptions = [...selectedOptions];
    if (foundOption) {
      newSelectedOptions = [
        ...newSelectedOptions.filter(selectedOption => selectedOption?.id !== option.id)
      ];
    } else {
      newSelectedOptions = [...newSelectedOptions, { ...option }];
    }
    setSelectedOptions(newSelectedOptions);
  };

  const handleClick = async (
    e: MouseEvent<HTMLInputElement | HTMLSpanElement> | Event
  ): Promise<void> => {
    e.stopPropagation();
    if (!disabled) {
      await toggleOpen();
    }
  };

  const fieldValue = value.map(option => option?.name || '').join(', ');
  const { onPseudoGeoOptionClick, allOptionsSelected } = usePseudoGeoOption({
    options,
    onOptionClick,
    selectedOptions,
    setSelectedOptions
  });
  return (
    <div
      ref={outerRef}
      className={`geounit-checkbox${isOpen && options.length > 0 ? ' geounit-checkbox--open' : ''}${
        disabled ? ' geounit-checkbox--disabled' : ''
      }`}
    >
      <div>
        <span className="geounit-checkbox__angle__wrapper" onClick={handleClick}>
          <FontAwesomeIcon className="geounit-checkbox__angle" icon={faAngleDown} />
        </span>
        <Input
          className={!!fieldValue ? 'input--filled' : ''}
          innerRef={inputRef}
          name={name || ''}
          placeholder={placeholder || ''}
          onClick={handleClick}
          value={staticLabel || valueToShow || fieldValue}
          readOnly
          disabled={disabled}
        />
      </div>
      {isLoading && (
        <div className="geounit-checkbox__loading">
          <Spinner />
        </div>
      )}
      {isOpen && options.length > 0 ? (
        <div className="geounit-checkbox__popover" ref={popupRef}>
          <div>
            <div className="geounit-checkbox__container">
              <div className="geounit-checkbox__container__inner">
                {options && withSelectAll && (
                  <CheckBox
                    id={'select-all'}
                    name={
                      selectedOptions.length === options.length
                        ? t(Keys.removeAll)
                        : t(Keys.selectAll)
                    }
                    onChange={(): void =>
                      setSelectedOptions(selectedOptions.length === options.length ? [] : options)
                    }
                    checked={selectedOptions.length === options.length}
                  />
                )}

                {options &&
                  options.map(option => {
                    const isPseudo = option.typeCode === TypeCodeKey.PSEUDO;
                    if (isPseudo && showPseudo) {
                      return (
                        <GeoUnitPseudo
                          onOptionClick={onPseudoGeoOptionClick}
                          option={option}
                          key={option.id}
                          selectedOptions={selectedOptions}
                          allOptionsSelected={allOptionsSelected}
                        />
                      );
                    }
                    return (
                      <CheckBox
                        key={option.id}
                        id={option.id}
                        name={option.name}
                        onChange={(): void => onOptionClick(option)}
                        checked={
                          !!selectedOptions.find(selectedOption => selectedOption?.id === option.id)
                        }
                      />
                    );
                  })}
              </div>
              <div className="geounit-checkbox__button-container d-flex justify-content-center mt-4 mb-2">
                {withRemoveAll && selectedOptions.length ? (
                  <button
                    type="button"
                    className="btn geounit-checkbox__btn-remove"
                    onClick={() => setSelectedOptions([])}
                  >
                    {t(Keys.removeAllChoices)}
                  </button>
                ) : null}
                <Button color="secondary" onClick={(): void => apply()}>
                  {t(Keys.apply)}
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const GeoUnitPseudo = ({
  option,
  onOptionClick,
  selectedOptions,
  allOptionsSelected
}: {
  option: ApiGeoUnit;
  onOptionClick: (a: ApiGeoUnit) => void;
  selectedOptions: ApiGeoUnit[];
  allOptionsSelected: boolean;
}) => {
  const { t } = useTranslation('geo-unit-checkbox-async', fallback, Keys);
  const geoUnitChildren = Object.keys(option.children).map(key => option.children[key]);
  const flattedGeoUnitChildren = geoUnitChildren.flatMap(itm => itm.code);
  const isPseudoChecked =
    option.code === PSEUDO_CODE.ALL
      ? allOptionsSelected
      : selectedOptions.filter(selectedOption =>
          flattedGeoUnitChildren.includes(selectedOption.code)
        ).length === flattedGeoUnitChildren.length;

  return (
    <div className="geounit-checkbox__container__group">
      <CheckBox
        id={option.id}
        onChange={() => onOptionClick(option)}
        name={t((Keys as Record<string, string>)[option.code])}
        checked={isPseudoChecked}
      />
      {geoUnitChildren.map(child => (
        <CheckBox
          id={child.id}
          key={child.id}
          onChange={() => onOptionClick(child)}
          name={child.name}
          checked={!!selectedOptions.find(selectedOption => selectedOption?.id === child.id)}
        />
      ))}
    </div>
  );
};

const CheckBox = ({
  id,
  checked,
  onChange,
  name
}: {
  id: string;
  checked?: boolean;
  onChange: () => void;
  name: string;
}) => {
  return (
    <div className="geounit-checkbox__field">
      <label>
        <CustomInput id={id} type="checkbox" checked={checked} onChange={onChange} />
        <span>{name}</span>
      </label>
    </div>
  );
};

export default GeoUnitCheckboxAsync;
